import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Grid,
    Typography,
    CircularProgress,
    Tabs,
    Tab,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    Divider,
    Stack,
    Chip,
    Button,
    useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { MainLayoutContainer } from '../components/common/ResponsiveLayout';
import EditProductDialog from '../components/product/EditProductDialog';
import DescriptionDetailsContent from '../components/description/DescriptionDetailsContent';
import ImageGenerationHistoryDialog from '../components/image_gen/ImageGenerationHistoryDialog';
import VideoGenerationHistoryDialog from '../components/video_gen/VideoGenerationHistoryDialog';
import FunnelHistoryDialog from '../components/funnel/FunnelHistoryDialog';
import ImagePreviewModal from '../components/product/ImagePreviewModal';
import ImageGenerationViewPage from '../pages/ImageGenerationViewPage';

import { getProducts, updateProduct, getGenerationsForProduct, getProductById, getVideoGenerationsForProduct } from '../services/api';
import { Product, ImageGeneration } from '../types';
import { useTranslation } from 'react-i18next';

const ProductCardPage: React.FC<{ selectedProductId?: number }> = ({ selectedProductId }) => {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const { t } = useTranslation();

    const [product, setProduct] = useState<Product | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [editOpen, setEditOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [imageGenerations, setImageGenerations] = useState<ImageGeneration[]>([]);
    const [videoGenerations, setVideoGenerations] = useState<any[]>([]);

    useEffect(() => {
        if (product?.id) {
            getVideoGenerationsForProduct(product.id)
                .then(gens => setVideoGenerations(gens ?? []))
                .catch(() => setVideoGenerations([]));
        } else {
            setVideoGenerations([]);
        }
    }, [product?.id]);

    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [videoDialogOpen, setVideoDialogOpen] = useState(false);
    const [funnelDialogOpen, setFunnelDialogOpen] = useState(false);

    const handleOpenImageDialog = () => setImageDialogOpen(true);
    const handleCloseImageDialog = () => setImageDialogOpen(false);

    const handleOpenVideoDialog = () => setVideoDialogOpen(true);
    const handleCloseVideoDialog = () => setVideoDialogOpen(false);

    const handleOpenFunnelDialog = () => setFunnelDialogOpen(true);
    const handleCloseFunnelDialog = () => setFunnelDialogOpen(false);

    const handleOpenImageGenDialog = () => {
        if (product) {
            navigate(`/dashboard/products/${product.id}/generate-images`);
        }
    };
    const handleOpenImageGenDialogForGeneration = (generation: any) => {
        if (product && generation) {
            navigate(`/dashboard/products/${product.id}/generate-images/${generation.id}`);
        }
    };
    const handleOpenVideoGenDialog = () => {
        if (product) {
            navigate(`/dashboard/products/${product.id}/generate-videos`);
        }
    };
    const handleOpenVideoGenDialogForGeneration = (generation: any) => {
        if (product && generation) {
            navigate(`/dashboard/products/${product.id}/generate-videos/${generation.id}`);
        }
    };

    const fetchProduct = useCallback(async (productId: number) => {
        setLoading(true);
        setError(null);
        try {
            const data = await getProductById(productId);
            if (!data) throw new Error(t('productCard.product_not_found'));
            setProduct(data);
        } catch (err: any) {
            console.error(t('productCard.failed_to_load_product'), err);
            setError(err?.message || t('productCard.failed_to_load_product'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        const pid = selectedProductId ?? (productId ? Number(productId) : undefined);
        if (pid === undefined || Number.isNaN(pid)) {
            setError(t('productCard.invalid_product_id'));
            setLoading(false);
            return;
        }
        fetchProduct(pid);
    }, [selectedProductId, productId, fetchProduct, t]);

    useEffect(() => {
        if (product?.id) {
            getGenerationsForProduct(product.id)
                .then(gens => setImageGenerations(gens ?? []))
                .catch(() => setImageGenerations([]));
        } else {
            setImageGenerations([]);
        }
    }, [product?.id]);

    const handleEditClose = () => setEditOpen(false);
    const handleEditOpen = () => setEditOpen(true);

    const handleProductUpdate = async (updated: Product) => {
        setProduct(updated);
        try {
            await updateProduct(updated.id, updated);
        } catch (err) {
            console.error(t('productCard.update_failed'), err);
        }
    };

    const handleImageClick = (url: string) => setPreviewImage(url);

    if (loading) {
        return (
            <MainLayoutContainer>
                <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
                    <CircularProgress />
                </Box>
            </MainLayoutContainer>
        );
    }

    if (error) {
        return (
            <MainLayoutContainer>
                <Box p={4}>
                    <Typography color="error">{error}</Typography>
                    <Button onClick={() => navigate('/products')} sx={{ mt: 2 }}>
                        {t('productCard.back_to_products')}
                    </Button>
                </Box>
            </MainLayoutContainer>
        );
    }

    if (!product) {
        return (
            <MainLayoutContainer>
                <Box p={4}>
                    <Typography>{t('productCard.product_not_found')}</Typography>
                </Box>
            </MainLayoutContainer>
        );
    }

    const renderGenerationRow = (
        title: string,
        generations: any[],
        onAddClick: (() => void) | null,
        onLoadGeneration: (gen: any) => void
    ) => {
        const maxThumbs = 3;
        return (
            <Box mb={3}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="h6">{title}</Typography>
                    {onAddClick && (
                        <Button size="small" startIcon={<AddIcon />} onClick={onAddClick}>
                            {t('common.new')}
                        </Button>
                    )}
                </Stack>
                {generations.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                        {t('productCard.no_generations')}
                    </Typography>
                ) : (
                    <Stack spacing={1}>
                        {generations.map((gen, idx) => {
                            let thumbs: JSX.Element[] = [];
                            if (Array.isArray(gen.image_requests) && gen.image_requests.length > 0) {
                                thumbs = gen.image_requests.map((req: any, i: number) => (
                                    <Box
                                        key={i}
                                        component="img"
                                        src={req.generated_image_url}
                                        alt={`gen-img-${i}`}
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 1,
                                            objectFit: 'cover',
                                            flexShrink: 0,
                                            border: `1px solid ${theme.palette.divider}`,
                                        }}
                                    />
                                ));
                            } else if (Array.isArray(gen.images) && gen.images.length > 0) {
                                thumbs = gen.images.map((img: string, i: number) => (
                                    <Box
                                        key={i}
                                        component="img"
                                        src={img}
                                        alt={`gen-img-${i}`}
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 1,
                                            objectFit: 'cover',
                                            flexShrink: 0,
                                            border: `1px solid ${theme.palette.divider}`,
                                        }}
                                    />
                                ));
                            } else if (gen.video_result_details?.video_url || gen.video_url) {
                                const url = gen.video_result_details?.video_url || gen.video_url;
                                thumbs = [
                                    <Box
                                        key={0}
                                        component="video"
                                        src={url}
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: 1,
                                            objectFit: 'cover',
                                            flexShrink: 0,
                                            border: `1px solid ${theme.palette.divider}`,
                                        }}
                                        muted
                                        playsInline
                                        autoPlay
                                        loop
                                        preload="metadata"
                                    />
                                ];
                            }
                            const extraCount = thumbs.length > maxThumbs ? thumbs.length - maxThumbs : 0;
                            return (
                                <Box
                                    key={gen.id ?? idx}
                                    onClick={() => onLoadGeneration(gen)}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'minmax(200px, 250px) 1fr 1fr 1fr 1fr 40px',
                                        columnGap: 4,
                                        alignItems: 'center',
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 1,
                                        p: 1.5,
                                        cursor: 'pointer',
                                        overflowX: 'auto',
                                        minWidth: '700px',
                                        '&:hover': { bgcolor: theme.palette.action.hover },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="nowrap">
                                            {thumbs.length === 0 && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {t('productCard.no_images')}
                                                </Typography>
                                            )}
                                            {thumbs.slice(0, maxThumbs)}
                                            {extraCount > 0 && (
                                                <Box
                                                    sx={{
                                                        width: 56,
                                                        height: 56,
                                                        borderRadius: 1,
                                                        bgcolor: theme.palette.grey[400],
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        fontSize: 14,
                                                        color: theme.palette.text.primary,
                                                        fontWeight: 600,
                                                        flexShrink: 0,
                                                        border: `1px solid ${theme.palette.divider}`,
                                                    }}
                                                >
                                                    +{extraCount}
                                                </Box>
                                            )}
                                        </Stack>
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {gen.created_at ? new Date(gen.created_at).toLocaleString() : ''}
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {gen.total_cost !== undefined
                                            ? `$${Number(gen.total_cost).toFixed(2)}`
                                            : gen.cost?.amount_usd !== undefined
                                                ? `$${Number(gen.cost.amount_usd).toFixed(2)}`
                                                : ''}
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {gen.marketplace || ''}
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" noWrap sx={{ textTransform: 'capitalize' }}>
                                        {gen.status || ''}
                                    </Typography>

                                    <IconButton size="small" onClick={e => e.stopPropagation()}>
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Box>
        );
    };

    return (
        <MainLayoutContainer>
            <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flex: '0 0 33%', pb: 2, position: 'relative' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', height: '100%', gap: 2 }}>
                        <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column', pr: 2 }}>
                            <Typography variant="h4" gutterBottom>
                                {product.name}
                            </Typography>
                            <Box sx={{ height: 155, overflowY: 'auto', pr: 2 }}>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
                                    {product.description || '—'}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: 200,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: theme.palette.grey[200],
                                    borderRadius: 1,
                                    mb: 1,
                                    border: `1px solid ${theme.palette.divider}`,
                                    cursor: product.images?.[0] ? 'pointer' : 'default',
                                    overflow: 'hidden',
                                }}
                                onClick={() => product.images?.[0] && handleImageClick(product.images[0])}
                            >
                                <Box
                                    component="img"
                                    src={product.images?.[0] ?? ''}
                                    alt="main"
                                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                            </Box>
                            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', justifyContent: 'flex-start', width: '100%' }}>
                                {product.images?.slice(1).map((img, i) => (
                                    <Box
                                        key={i}
                                        component="img"
                                        src={img}
                                        alt={`thumb-${i}`}
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            objectFit: 'cover',
                                            borderRadius: 1,
                                            border: `1px solid ${theme.palette.divider}`,
                                            cursor: 'pointer',
                                            flexShrink: 0,
                                        }}
                                        onClick={() => handleImageClick(img)}
                                    />
                                ))}
                            </Stack>
                        </Box>
                    </Box>
                    <Box sx={{ position: 'absolute', left: 0, bottom: 30, zIndex: 2 }}>
                        <Button size="small" startIcon={<EditIcon />} onClick={handleEditOpen}>
                            {t('productCard.edit')}
                        </Button>
                    </Box>
                </Box>
                <Divider />
                <Box sx={{ flex: '1 1 67%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: theme => theme.palette.background.default }}>
                        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} aria-label="AI content tabs" sx={{ mb: 0 }}>
                            <Tab label={t('productCard.photos')} />
                            <Tab label={t('productCard.videos')} />
                            <Tab label={t('productCard.funnels')} />
                        </Tabs>
                    </Box>
                    <Box sx={{ flex: 1, overflowY: 'auto', pt: 2 }}>
                        {activeTab === 0 && (
                            <Box mb={2}>
                                <Button size="small" startIcon={<AddIcon />} onClick={handleOpenImageGenDialog}>
                                    {t('common.new')}
                                </Button>
                                {renderGenerationRow('', imageGenerations, null, gen => handleOpenImageGenDialogForGeneration(gen))}
                            </Box>
                        )}
                        {activeTab === 1 && (
                            <Box mb={2}>
                                <Button size="small" startIcon={<AddIcon />} onClick={handleOpenVideoGenDialog}>
                                    {t('common.new')}
                                </Button>
                                {renderGenerationRow('', videoGenerations, null, gen => handleOpenVideoGenDialogForGeneration(gen))}
                            </Box>
                        )}
                        {activeTab === 2 && (
                            <Box mb={2}>
                                <Button size="small" startIcon={<AddIcon />} onClick={handleOpenFunnelDialog}>
                                    {t('common.new')}
                                </Button>
                                {renderGenerationRow('', product.funnelGenerations ?? [], null, gen => console.log('Load funnel generation', gen))}
                            </Box>
                        )}
                    </Box>
                </Box>

                <ImagePreviewModal open={Boolean(previewImage)} imageUrl={previewImage ?? ''} onClose={() => setPreviewImage(null)} />
                <EditProductDialog
                    open={editOpen}
                    product={product}
                    onClose={handleEditClose}
                    onSuccess={async () => {
                        handleEditClose();
                        if (product) await fetchProduct(product.id);
                    }}
                />
                <ImageGenerationHistoryDialog
                    open={imageDialogOpen}
                    onClose={handleCloseImageDialog}
                    historyGenerations={product.imageGenerations ?? []}
                    onLoadGeneration={gen => console.log('Load generation', gen)}
                />
                <VideoGenerationHistoryDialog
                    open={videoDialogOpen}
                    onClose={handleCloseVideoDialog}
                    historyGenerations={product.videoGenerations || []}
                    onLoadGeneration={gen => console.log('Load video generation', gen)}
                />
                <FunnelHistoryDialog
                    open={funnelDialogOpen}
                    onClose={handleCloseFunnelDialog}
                    historyGenerations={product.funnelGenerations || []}
                    onLoadGeneration={gen => console.log('Load funnel generation', gen)}
                    isLoading={false}
                />
            </Box>
        </MainLayoutContainer>
    );
};

export default ProductCardPage;
